// PublicReport.jsx — no auth required, accessed via /report/:token
import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { get } from '../lib/api'

const GOAL_COLORS = ['#4F46E5','#059669','#D97706','#7C3AED','#DB2777']
const GOAL_BG     = ['#EEF2FF','#ECFDF5','#FFFBEB','#F5F3FF','#FDF2F8']
const GOAL_LIGHT  = ['#E0E7FF','#D1FAE5','#FEF3C7','#EDE9FE','#FCE7F3']
function gc(i)  { return GOAL_COLORS[i % GOAL_COLORS.length] }
function gbg(i) { return GOAL_BG[i % GOAL_BG.length] }
function scoreColor(s) { return !s ? '#94A3B8' : s >= 8 ? '#059669' : s >= 6 ? '#4F46E5' : s >= 4 ? '#D97706' : '#EF4444' }
function scoreLabel(s) { return !s ? '—' : s >= 8 ? 'Outstanding' : s >= 6 ? 'Good' : s >= 4 ? 'Average' : 'Below Average' }
function fmtDateTime(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-US', { day:'numeric', month:'long', year:'numeric', hour:'2-digit', minute:'2-digit' })
}

function PieChart({ goals }) {
  const total = goals.reduce((s, g) => s + (g.weightage || 0), 0)
  if (!total) return null
  const size = 160, cx = 80, cy = 80, r = 65
  let cum = -Math.PI / 2
  const slices = goals.map((g, i) => {
    const angle = (g.weightage / total) * 2 * Math.PI
    const sa = cum, ea = cum + angle; cum = ea
    const x1 = cx + r * Math.cos(sa), y1 = cy + r * Math.sin(sa)
    const x2 = cx + r * Math.cos(ea), y2 = cy + r * Math.sin(ea)
    const mid = sa + angle / 2
    return {
      d: `M${cx} ${cy} L${x1.toFixed(1)} ${y1.toFixed(1)} A${r} ${r} 0 ${angle > Math.PI ? 1 : 0} 1 ${x2.toFixed(1)} ${y2.toFixed(1)}Z`,
      lx: cx + r * 0.65 * Math.cos(mid), ly: cy + r * 0.65 * Math.sin(mid),
      show: g.weightage / total > 0.09, label: `${g.weightage}%`, color: gc(i),
    }
  })
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
      {slices.map((s, i) => (
        <g key={i}>
          <path d={s.d} fill={s.color} stroke="#fff" strokeWidth={2.5} />
          {s.show && <text x={s.lx.toFixed(1)} y={s.ly.toFixed(1)} textAnchor="middle"
            dominantBaseline="middle" fill="#fff" fontSize={10} fontWeight="800">{s.label}</text>}
        </g>
      ))}
    </svg>
  )
}

function BarChart({ goals }) {
  const bh = 28, gap = 9, lw = 52, bw = 190, sw = 50
  const h = goals.length * (bh + gap)
  return (
    <svg viewBox={`0 0 ${lw + bw + sw} ${h}`} width={lw + bw + sw} height={h}>
      {goals.map((g, i) => {
        const y = i * (bh + gap)
        const fw = ((g.employer_score || 0) / 10) * bw
        return (
          <g key={i}>
            <text x={lw - 6} y={y + bh / 2 + 4} textAnchor="end" fontSize={10} fill="#64748B">G{g.goal_number}</text>
            <rect x={lw} y={y} width={bw} height={bh} rx={5} fill="#F1F5F9" />
            {g.employer_score > 0 && <rect x={lw} y={y} width={fw} height={bh} rx={5} fill={gc(i)} />}
            <text x={lw + bw + 6} y={y + bh / 2 + 5} fontSize={12} fontWeight="800"
              fill={g.employer_score ? scoreColor(g.employer_score) : '#94A3B8'}>
              {g.employer_score ? `${g.employer_score}/10` : '—'}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

export default function PublicReport() {
  const { token }       = useParams()
  const [review, setReview]   = useState(null)
  const [employee, setEmployee] = useState(null)
  const [loading, setLoading]  = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [copied, setCopied]    = useState(false)

  useEffect(() => {
    async function load() {
      try {
        const res = await get(`/public/review/${token}`)
        setReview(res.review)
        setEmployee(res.review.employee)
      } catch {
        setNotFound(true)
      }
      setLoading(false)
    }
    load()
  }, [token])

  function handlePrint() {
    window.print()
  }

  function handleCopyLink() {
    navigator.clipboard.writeText(window.location.href)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  function handleShare(platform) {
    const url  = window.location.href
    const name = employee?.full_name || 'Employee'
    const org  = employee?.organizations?.name || ''
    const period = review?.review_period || `${review?.year || ''} Review`
    const score  = review?.final_score
    const text = `🎯 ${name}'s Performance Development Review — ${period}\n🏢 ${org}\n${score ? `⭐ Score: ${score}/10 — ${scoreLabel(score)}\n` : ''}🔗 View full report: ${url}\n\nPowered by Employee Hive | www.employeehive.com`
    const enc  = encodeURIComponent(text)
    const urlEnc = encodeURIComponent(url)

    const urls = {
      whatsapp: `https://wa.me/?text=${enc}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${urlEnc}`,
      twitter:  `https://twitter.com/intent/tweet?text=${enc}`,
      email:    `mailto:?subject=${encodeURIComponent(`Performance Review — ${name}`)}&body=${enc}`,
    }
    if (urls[platform]) window.open(urls[platform], '_blank')
  }

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8F9FA', fontFamily: 'Inter, system-ui' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 48, height: 48, borderRadius: 10, background: 'linear-gradient(135deg,#4F46E5,#6366F1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', fontSize: 18, fontWeight: 900, color: '#fff' }}>EH</div>
        <p style={{ color: '#64748B', fontSize: 14 }}>Loading report…</p>
      </div>
    </div>
  )

  if (notFound) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8F9FA', fontFamily: 'Inter, system-ui' }}>
      <div style={{ textAlign: 'center', maxWidth: 400, padding: '2rem' }}>
        <div style={{ fontSize: 52, marginBottom: '1rem' }}>🔒</div>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1E293B', marginBottom: 8 }}>Report not found</h2>
        <p style={{ fontSize: 14, color: '#64748B', lineHeight: 1.6 }}>This link may have expired or the report hasn't been shared publicly yet.</p>
        <div style={{ marginTop: '1.5rem', fontSize: 12, color: '#94A3B8' }}>Powered by <strong style={{ color: '#4F46E5' }}>Employee Hive</strong></div>
      </div>
    </div>
  )

  const entries      = (review.review_goal_entries || []).sort((a, b) => a.goal_number - b.goal_number)
  const displayScore = parseFloat(review.final_score) ||
    (entries.every(e => e.employer_score)
      ? parseFloat((entries.reduce((s, e) => s + e.employer_score * (e.weightage / 100), 0)).toFixed(1))
      : null)
  const sc = displayScore ? scoreColor(displayScore) : '#64748B'
  const sl = displayScore ? scoreLabel(displayScore) : ''

  return (
    <div style={{ minHeight: '100vh', background: '#F1F5F9', fontFamily: 'Inter, system-ui, sans-serif' }}>

      {/* ── Top nav bar ── */}
      <div style={{ background: 'linear-gradient(135deg,#1E293B,#0F172A)', padding: '0 2rem', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 8, background: 'linear-gradient(135deg,#4F46E5,#6366F1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 900, color: '#fff' }}>EH</div>
          <div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', letterSpacing: '.08em' }}>EMPLOYEE HIVE</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>Performance Review Report</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={handleCopyLink}
            style={{ padding: '7px 14px', background: copied ? '#10B981' : 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#fff', cursor: 'pointer' }}>
            {copied ? '✅ Link copied!' : '🔗 Copy link'}
          </button>
          <button onClick={() => handleShare('whatsapp')}
            style={{ padding: '7px 14px', background: '#25D366', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#fff', cursor: 'pointer' }}>
            💬 WhatsApp
          </button>
          <button onClick={() => handleShare('linkedin')}
            style={{ padding: '7px 14px', background: '#0A66C2', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#fff', cursor: 'pointer' }}>
            🔗 LinkedIn
          </button>
          <button onClick={() => handleShare('twitter')}
            style={{ padding: '7px 14px', background: '#000', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#fff', cursor: 'pointer' }}>
            𝕏 Twitter
          </button>
          <button onClick={() => handleShare('email')}
            style={{ padding: '7px 14px', background: '#EA4335', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#fff', cursor: 'pointer' }}>
            📧 Email
          </button>
          <button onClick={handlePrint}
            style={{ padding: '7px 14px', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#fff', cursor: 'pointer' }}>
            🖨️ Print / PDF
          </button>
        </div>
      </div>

      {/* ── Report ── */}
      <div id="report-printable" style={{ maxWidth: 860, margin: '2rem auto', padding: '0 1rem' }}>

        {/* Header card */}
        <div style={{ background: 'linear-gradient(135deg,#1E293B,#0F172A)', borderRadius: 16, padding: '1.75rem 2rem', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'linear-gradient(135deg,#4F46E5,#6366F1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 800, color: '#fff', flexShrink: 0 }}>
              {employee?.full_name?.charAt(0)?.toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 900, color: '#fff', letterSpacing: '-0.5px' }}>{employee?.full_name}</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>{employee?.position || employee?.email}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>{employee?.organizations?.name}</div>
            </div>
          </div>
          {displayScore && (
            <div style={{ textAlign: 'center', background: `${sc}20`, border: `2px solid ${sc}50`, borderRadius: 14, padding: '1rem 1.5rem' }}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', letterSpacing: '.08em', marginBottom: 4 }}>FINAL SCORE</div>
              <div style={{ fontSize: 48, fontWeight: 900, color: sc, lineHeight: 1 }}>{displayScore}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>out of 10</div>
              <div style={{ fontSize: 13, fontWeight: 800, color: sc, marginTop: 4 }}>{sl}</div>
            </div>
          )}
        </div>

        {/* Meta row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 20 }}>
          {[
            ['Review Period', review.review_period || `Review ${review.year || ''}`],
            ['Goals',         `${entries.length} goal${entries.length !== 1 ? 's' : ''}`],
            ['Submitted',     fmtDateTime(review.submitted_at)],
            ['Signed Off',    fmtDateTime(review.signed_off_at)],
          ].map(([l, v]) => (
            <div key={l} style={{ background: '#fff', borderRadius: 10, padding: '12px 14px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4 }}>{l}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1E293B' }}>{v}</div>
            </div>
          ))}
        </div>

        {/* Charts */}
        {displayScore && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
            <div style={{ background: '#fff', borderRadius: 14, padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1E293B', marginBottom: '1rem' }}>Goal Weightage</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <PieChart goals={entries} />
                <div>
                  {entries.map((g, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <div style={{ width: 10, height: 10, borderRadius: 3, background: gc(i), flexShrink: 0 }} />
                      <span style={{ fontSize: 12, color: '#475569' }}>G{g.goal_number}: {g.weightage}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ background: '#fff', borderRadius: 14, padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1E293B', marginBottom: '1rem' }}>Score per Goal</div>
              <BarChart goals={entries} />
            </div>
          </div>
        )}

        {/* Goals */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#1E293B', marginBottom: 12, paddingBottom: 8, borderBottom: '2px solid #E5E7EB' }}>
            📋 Goal-by-Goal Breakdown
          </div>
          {entries.map((g, i) => {
            const evLinks = (g.evidence_links || []).filter(ev => ev.url)
            return (
              <div key={i} style={{ background: '#fff', border: `1px solid #E5E7EB`, borderLeft: `5px solid ${gc(i)}`, borderRadius: '0 12px 12px 0', marginBottom: 14, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ background: gbg(i), padding: '10px 16px', borderBottom: `1px solid ${gc(i)}22`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 30, height: 30, borderRadius: '50%', background: gc(i), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 900, color: '#fff', flexShrink: 0 }}>{g.goal_number}</div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: '#1E293B' }}>{g.goal_title || `Goal ${g.goal_number}`}</div>
                      <div style={{ fontSize: 11, color: '#64748B' }}>Goal {g.goal_number} of {entries.length}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: gc(i), background: '#fff', padding: '3px 10px', borderRadius: 99, border: `1px solid ${gc(i)}30` }}>{g.weightage}% weight</span>
                    {g.employer_score && (
                      <div style={{ textAlign: 'center', background: `${scoreColor(g.employer_score)}10`, borderRadius: 8, padding: '4px 12px', border: `1px solid ${scoreColor(g.employer_score)}30` }}>
                        <div style={{ fontSize: 18, fontWeight: 900, color: scoreColor(g.employer_score), lineHeight: 1 }}>{g.employer_score}/10</div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: scoreColor(g.employer_score) }}>{scoreLabel(g.employer_score)}</div>
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ padding: '14px 16px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: evLinks.length || g.employer_comment ? 10 : 0 }}>
                    <div style={{ background: '#F8F9FA', borderRadius: 8, padding: '10px 12px' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 5 }}>📋 What was planned</div>
                      <p style={{ fontSize: 13, color: '#475569', margin: 0, lineHeight: 1.6 }}>{g.goal_description || '—'}</p>
                      {g.kpi && <p style={{ fontSize: 12, color: '#64748B', margin: '5px 0 0' }}><strong>KPI:</strong> {g.kpi}</p>}
                    </div>
                    <div style={{ background: '#F8F9FA', borderRadius: 8, padding: '10px 12px' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 5 }}>✅ Accomplished</div>
                      <p style={{ fontSize: 13, color: '#1E293B', margin: 0, lineHeight: 1.6 }}>{g.what_was_done || '—'}</p>
                    </div>
                  </div>
                  {evLinks.length > 0 && (
                    <div style={{ marginBottom: g.employer_comment ? 10 : 0 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 6 }}>📁 Evidence</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {evLinks.map((ev, ei) => (
                          <a key={ei} href={ev.url} target="_blank" rel="noreferrer"
                            style={{ fontSize: 12, fontWeight: 600, color: '#4F46E5', background: '#EEF2FF', padding: '4px 12px', borderRadius: 99, textDecoration: 'none', border: '1px solid #C7D2FE' }}>
                            {ev.label || 'View evidence'}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  {g.employer_comment && (
                    <div style={{ background: '#F0F0FF', borderRadius: 8, padding: '8px 12px' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#4F46E5', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4 }}>Manager Comment</div>
                      <p style={{ fontSize: 13, color: '#1E293B', margin: 0, lineHeight: 1.7 }}>{g.employer_comment}</p>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {/* Manager assessment */}
        {(review.employer_final_comment || review.improvement_areas) && (
          <div style={{ background: '#F0F0FF', border: '1px solid #C7D2FE', borderRadius: 14, padding: '1.25rem', marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#4F46E5', marginBottom: '1rem' }}>💬 Manager's Overall Assessment</div>
            {review.employer_final_comment && (
              <div style={{ marginBottom: review.improvement_areas ? 12 : 0 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4 }}>Overall Comment</div>
                <p style={{ fontSize: 13, color: '#1E293B', margin: 0, lineHeight: 1.7 }}>{review.employer_final_comment}</p>
              </div>
            )}
            {review.improvement_areas && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#D97706', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4 }}>Areas for Improvement</div>
                <p style={{ fontSize: 13, color: '#1E293B', margin: 0, lineHeight: 1.7 }}>{review.improvement_areas}</p>
              </div>
            )}
          </div>
        )}

        {/* Sign-off */}
        <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 14, padding: '1.25rem', marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#059669', marginBottom: '1rem' }}>✅ Sign-off Record</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ background: '#fff', borderRadius: 10, padding: '12px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 5 }}>Employee</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1E293B', marginBottom: 2 }}>{employee?.full_name}</div>
              <div style={{ fontSize: 11, color: '#64748B', marginBottom: 6 }}>Signed: {fmtDateTime(review.signed_off_at)}</div>
              {review.employee_sign_off_comment && (
                <p style={{ fontSize: 12, color: '#475569', margin: 0, lineHeight: 1.6, fontStyle: 'italic' }}>"{review.employee_sign_off_comment}"</p>
              )}
            </div>
            <div style={{ background: '#fff', borderRadius: 10, padding: '12px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 5 }}>Manager / Employer</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1E293B', marginBottom: 2 }}>{employee?.organizations?.name}</div>
              <div style={{ fontSize: 11, color: '#64748B' }}>Reviewed: {fmtDateTime(review.employer_reviewed_at)}</div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', padding: '1rem', borderTop: '1px solid #E5E7EB' }}>
          <div style={{ fontSize: 12, color: '#94A3B8', marginBottom: 4 }}>
            Generated by <strong style={{ color: '#4F46E5' }}>Employee Hive</strong> · {fmtDateTime(new Date().toISOString())}
          </div>
          <a href="https://www.employeehive.com" target="_blank" rel="noreferrer"
            style={{ fontSize: 13, color: '#4F46E5', fontWeight: 700, textDecoration: 'none' }}>
            www.employeehive.com
          </a>
        </div>

        {/* Print styles */}
        <style>{`
          @media print {
            body > *:not(#report-printable) { display: none !important; }
            #report-printable { position: fixed; top: 0; left: 0; width: 100%; }
            .no-print { display: none !important; }
            @page { margin: 12mm; }
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          }
        `}</style>
      </div>
    </div>
  )
}
